import React from 'react';
import type { FtueDiagramKey } from './types';

/** Technical-blueprint mini diagrams — Ops Ink lines, Signal Magenta accents. */
export function FtueBlueprint({
  kind,
  className,
}: {
  kind: FtueDiagramKey;
  className?: string;
}) {
  return (
    <svg
      className={className ?? 'ftue-blueprint'}
      viewBox="0 0 120 64"
      width={120}
      height={64}
      aria-hidden
    >
      <defs>
        <pattern
          id="ftue-grid"
          width="8"
          height="8"
          patternUnits="userSpaceOnUse"
        >
          <path
            d="M8 0H0V8"
            fill="none"
            stroke="currentColor"
            strokeWidth="0.4"
            opacity="0.18"
          />
        </pattern>
      </defs>
      <rect
        x="0.5"
        y="0.5"
        width="119"
        height="63"
        rx="4"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.75"
        opacity="0.35"
      />
      <rect
        x="4"
        y="4"
        width="112"
        height="56"
        fill="url(#ftue-grid)"
        opacity="0.55"
      />
      {diagramInner(kind)}
    </svg>
  );
}

function diagramInner(kind: FtueDiagramKey): React.ReactNode {
  switch (kind) {
    case 'nav':
      return (
        <>
          <rect x="10" y="10" width="18" height="44" rx="2" className="ftue-bp-ink" />
          <rect x="14" y="16" width="10" height="4" rx="1" className="ftue-bp-accent" />
          <rect x="14" y="24" width="10" height="4" rx="1" className="ftue-bp-ink-soft" />
          <rect x="14" y="32" width="10" height="4" rx="1" className="ftue-bp-ink-soft" />
          <rect x="36" y="10" width="74" height="10" rx="2" className="ftue-bp-ink-soft" />
          <rect x="36" y="26" width="74" height="28" rx="2" className="ftue-bp-ink" />
        </>
      );
    case 'scope':
      return (
        <>
          <rect x="18" y="20" width="84" height="24" rx="12" className="ftue-bp-ink" />
          <circle cx="32" cy="32" r="5" className="ftue-bp-accent" />
          <rect x="42" y="27" width="40" height="5" rx="1" className="ftue-bp-ink-soft" />
          <rect x="42" y="35" width="24" height="3" rx="1" className="ftue-bp-ink-soft" />
        </>
      );
    case 'period':
      return (
        <>
          <rect x="14" y="14" width="92" height="36" rx="4" className="ftue-bp-ink" />
          {Array.from({ length: 7 }, (_, i) => (
            <rect
              key={i}
              x={22 + i * 12}
              y="24"
              width="8"
              height="16"
              rx="1"
              className={i === 4 ? 'ftue-bp-accent' : 'ftue-bp-ink-soft'}
            />
          ))}
        </>
      );
    case 'status':
      return (
        <>
          <rect x="16" y="18" width="40" height="12" rx="6" className="ftue-bp-accent" />
          <rect x="64" y="18" width="40" height="12" rx="6" className="ftue-bp-ink" />
          <rect x="16" y="36" width="88" height="10" rx="2" className="ftue-bp-ink-soft" />
        </>
      );
    case 'performance-story':
      return (
        <>
          <rect x="12" y="12" width="44" height="40" rx="3" className="ftue-bp-ink" />
          <rect x="18" y="18" width="32" height="6" rx="1" className="ftue-bp-accent" />
          <rect x="18" y="28" width="28" height="4" rx="1" className="ftue-bp-ink-soft" />
          <rect x="18" y="36" width="22" height="4" rx="1" className="ftue-bp-ink-soft" />
          <rect x="64" y="12" width="44" height="18" rx="3" className="ftue-bp-ink" />
          <rect x="64" y="34" width="44" height="18" rx="3" className="ftue-bp-ink-soft" />
        </>
      );
    case 'scorecard':
      return (
        <>
          {[0, 1, 2].map(i => (
            <rect
              key={i}
              x={14 + i * 34}
              y="16"
              width="28"
              height="32"
              rx="3"
              className={i === 1 ? 'ftue-bp-accent' : 'ftue-bp-ink'}
            />
          ))}
        </>
      );
    case 'drill':
      return (
        <>
          <rect x="10" y="10" width="100" height="12" rx="2" className="ftue-bp-ink-soft" />
          <rect x="10" y="28" width="62" height="26" rx="3" className="ftue-bp-ink" />
          <rect x="78" y="28" width="32" height="26" rx="3" className="ftue-bp-accent" />
          <path
            d="M18 46 L30 34 L42 40 L54 28"
            className="ftue-bp-accent-stroke"
          />
        </>
      );
    case 'ai':
      return (
        <>
          <rect x="58" y="8" width="52" height="48" rx="3" className="ftue-bp-ink" />
          <circle cx="84" cy="22" r="6" className="ftue-bp-accent" />
          <rect x="66" y="34" width="36" height="4" rx="1" className="ftue-bp-ink-soft" />
          <rect x="66" y="42" width="28" height="4" rx="1" className="ftue-bp-ink-soft" />
          <rect x="10" y="14" width="40" height="36" rx="3" className="ftue-bp-ink-soft" />
        </>
      );
    case 'timeline':
      return (
        <>
          <path
            d="M12 44 C28 44 32 20 48 20 C64 20 68 40 84 40 C96 40 100 28 108 28"
            className="ftue-bp-accent-stroke"
          />
          <line x1="60" y1="12" x2="60" y2="52" className="ftue-bp-ink-stroke" />
          <circle cx="60" cy="32" r="3" className="ftue-bp-accent" />
        </>
      );
    case 'recovery':
      return (
        <>
          <rect x="14" y="14" width="40" height="36" rx="3" className="ftue-bp-ink" />
          <rect x="66" y="14" width="40" height="36" rx="3" className="ftue-bp-accent" />
          <path d="M54 32 H66 M62 28 L66 32 L62 36" className="ftue-bp-accent-stroke" />
        </>
      );
    case 'settings-matrix':
      return (
        <>
          {Array.from({ length: 4 }, (_, row) =>
            Array.from({ length: 5 }, (_, col) => (
              <circle
                key={`${row}-${col}`}
                cx={22 + col * 18}
                cy={16 + row * 12}
                r="3.5"
                className={
                  (row + col) % 3 === 0 ? 'ftue-bp-accent' : 'ftue-bp-ink-soft'
                }
              />
            )),
          )}
        </>
      );
    case 'sources':
      return (
        <>
          <rect x="20" y="14" width="80" height="36" rx="4" className="ftue-bp-ink" />
          <rect x="28" y="22" width="18" height="20" rx="2" className="ftue-bp-accent" />
          <rect x="52" y="22" width="18" height="20" rx="2" className="ftue-bp-ink-soft" />
          <rect x="76" y="22" width="16" height="20" rx="2" className="ftue-bp-ink-soft" />
        </>
      );
    case 'compare':
      return (
        <>
          <rect x="14" y="16" width="40" height="32" rx="3" className="ftue-bp-ink" />
          <rect x="66" y="16" width="40" height="32" rx="3" className="ftue-bp-accent" />
          <rect x="22" y="24" width="24" height="4" rx="1" className="ftue-bp-ink-soft" />
          <rect x="74" y="24" width="24" height="4" rx="1" className="ftue-bp-ink-soft" />
        </>
      );
    case 'north-star':
      return (
        <>
          <polygon
            points="60,10 68,28 88,30 72,42 76,58 60,48 44,58 48,42 32,30 52,28"
            className="ftue-bp-accent"
          />
        </>
      );
    case 'assistant-ask':
      return (
        <>
          <rect x="14" y="16" width="92" height="22" rx="11" className="ftue-bp-ink" />
          <rect x="24" y="24" width="48" height="6" rx="2" className="ftue-bp-ink-soft" />
          <circle cx="92" cy="27" r="5" className="ftue-bp-accent" />
          <rect x="22" y="46" width="28" height="8" rx="4" className="ftue-bp-accent" />
          <rect x="54" y="46" width="24" height="8" rx="4" className="ftue-bp-ink-soft" />
          <rect x="82" y="46" width="16" height="8" rx="4" className="ftue-bp-ink-soft" />
        </>
      );
    case 'assistant-notes':
      return (
        <>
          <rect x="16" y="10" width="88" height="44" rx="3" className="ftue-bp-ink" />
          <rect x="24" y="16" width="36" height="5" rx="1" className="ftue-bp-accent" />
          <rect x="24" y="26" width="72" height="4" rx="1" className="ftue-bp-ink-soft" />
          <rect x="24" y="34" width="58" height="4" rx="1" className="ftue-bp-ink-soft" />
          <rect x="24" y="42" width="44" height="4" rx="1" className="ftue-bp-ink-soft" />
        </>
      );
    case 'assistant-pin':
      return (
        <>
          <rect x="12" y="14" width="44" height="36" rx="3" className="ftue-bp-ink" />
          <rect x="18" y="20" width="20" height="5" rx="1" className="ftue-bp-accent" />
          <rect x="18" y="30" width="32" height="4" rx="1" className="ftue-bp-ink-soft" />
          <rect x="18" y="38" width="24" height="4" rx="1" className="ftue-bp-ink-soft" />
          <rect x="64" y="14" width="44" height="16" rx="3" className="ftue-bp-accent" />
          <rect x="64" y="34" width="44" height="16" rx="3" className="ftue-bp-ink-soft" />
        </>
      );
    default:
      return (
        <>
          <rect x="20" y="18" width="80" height="28" rx="4" className="ftue-bp-ink" />
          <rect x="30" y="28" width="40" height="6" rx="1" className="ftue-bp-accent" />
        </>
      );
  }
}
